import { useEffect, useMemo, useState } from "react";
import { Select } from "../../../../shared";

/**
 * Builds mileage options from route and leg data.
 * @param {Object} params - Option builder params.
 * @param {Object} params.route - Route data with mileage fields.
 * @param {number} params.totalMiles - Current total miles for the leg.
 * @param {boolean} params.useCustomMileage - Whether custom mileage is enabled.
 * @param {number|null} params.customMileage - Custom mileage value.
 * @returns {Array} Mileage options for the selector.
 */
export function buildMileageOptions({
  route,
  totalMiles,
  useCustomMileage,
  customMileage,
}) {
  const options = [];
  const seenValues = new Set();

  // Default mileage - the original route-derived distance
  const defaultMileage = route?.defaultMileage ?? route?.mileage ?? null;

  if (defaultMileage != null && defaultMileage > 0) {
    options.push({
      id: "defaultMileage",
      label: `Default Mileage (${defaultMileage} mi)`,
      value: defaultMileage,
    });
    seenValues.add(defaultMileage);
  }

  // Custom/billed mileage from route or props
  const routeCustomMileage = route?.customMileage;
  const resolvedCustomMileage = useCustomMileage
    ? customMileage
    : routeCustomMileage;

  if (
    resolvedCustomMileage != null &&
    resolvedCustomMileage > 0 &&
    !seenValues.has(resolvedCustomMileage)
  ) {
    options.push({
      id: "billedMileage",
      label: `Billed Mileage (${resolvedCustomMileage} mi)`,
      value: resolvedCustomMileage,
    });
    seenValues.add(resolvedCustomMileage);
  }

  // Billed miles from route (only show if it differs from defaultMileage)
  if (
    route?.billedMiles != null &&
    route.billedMiles > 0 &&
    !seenValues.has(route.billedMiles)
  ) {
    options.push({
      id: "billedMiles",
      label: `Billed Miles (${route.billedMiles} mi)`,
      value: route.billedMiles,
    });
    seenValues.add(route.billedMiles);
  }

  // If totalMiles differs from defaultMileage and isn't already in options,
  // add it as "Current Mileage" (for backward compatibility with existing data)
  if (
    typeof totalMiles === "number" &&
    totalMiles > 0 &&
    !seenValues.has(totalMiles)
  ) {
    // Only add if there's a default and it differs
    if (defaultMileage != null && defaultMileage > 0 && totalMiles !== defaultMileage) {
      options.push({
        id: "currentMileage",
        label: `Billed Mileage (${totalMiles} mi)`,
        value: totalMiles,
      });
      seenValues.add(totalMiles);
    }
  }

  return options;
}

/**
 * Resolves the currently selected mileage value for the selector.
 * Respects `useCustomMileage` flag: when false, always returns
 * defaultMileage so the dropdown shows "Default Mileage" selected.
 * @param {Object} params - Current mileage params.
 * @param {Object} params.route - Route data with mileage fields.
 * @param {number} params.totalMiles - Current total miles for the leg.
 * @param {boolean} params.useCustomMileage - Whether custom mileage is enabled.
 * @param {number|null} params.customMileage - Custom mileage value.
 * @returns {number} Mileage value to display.
 */
export function getCurrentMileageValue({
  route,
  totalMiles,
  useCustomMileage,
  customMileage,
  mileageSelectionType,
}) {
  const normalizedMileageSelectionType = String(mileageSelectionType || "")
    .toLowerCase()
    .trim();
  const selectionIndicatesCustom = [
    "billedmileage",
    "billedmiles",
    "custominput",
    "currentmileage",
  ].includes(normalizedMileageSelectionType);
  const defaultMileage = route?.defaultMileage ?? route?.mileage ?? 0;
  const hasLegacyCustomSelection =
    normalizedMileageSelectionType === "" &&
    typeof totalMiles === "number" &&
    totalMiles > 0 &&
    defaultMileage > 0 &&
    totalMiles !== defaultMileage;
  const shouldUseCustomMileage =
    !!useCustomMileage || selectionIndicatesCustom || hasLegacyCustomSelection;

  // ── Custom mileage explicitly enabled ──
  if (shouldUseCustomMileage) {
    if (customMileage != null && customMileage > 0) return customMileage;
    if (route?.billedMiles != null && route.billedMiles > 0)
      return route.billedMiles;
    if (route?.customMileage != null && route.customMileage > 0)
      return route.customMileage;
    if (typeof totalMiles === "number" && totalMiles > 0) return totalMiles;
    // Custom flag set but no explicit value – fall through to default
  }

  // ── Default mileage (route-derived distance) ──
  if (defaultMileage > 0) return defaultMileage;

  // Fall back to totalMiles (current leg value)
  if (typeof totalMiles === "number" && totalMiles > 0) return totalMiles;

  // Finally fall back to route defaults
  if (route) return route.mileage || route.defaultMileage || 0;

  return 0;
}

/**
 * Mileage selector for leg create/edit.
 * Shows a dropdown of available mileage options (default, billed, custom)
 * and lets the user pick one or type a custom value.
 * The input displays the numeric value for easy editing; the dropdown
 * options show the mileage type labels (e.g. "Default Mileage (120 mi)").
 * @param {Object} props - Component props.
 * @param {Object} props.route - Route data with mileage fields.
 * @param {number} props.totalMiles - Current total miles value.
 * @param {boolean} props.useCustomMileage - Whether custom mileage is active.
 * @param {number|null} props.customMileage - Custom mileage value.
 * @param {Function} props.onChange - Mileage change handler.
 * @param {string} props.size - Select size.
 */
const LegMileageSelector = ({
  route,
  totalMiles,
  useCustomMileage,
  customMileage,
  mileageSelectionType,
  onChange,
  size = "sm",
  showLabel = true,
}) => {
  const routeDefaultMileage = route?.defaultMileage;
  const routeMileage = route?.mileage;
  const routeCustomMileage = route?.customMileage;
  const routeBilledMiles = route?.billedMiles;

  const mileageOptions = useMemo(
    () =>
      buildMileageOptions({
        route,
        totalMiles,
        useCustomMileage,
        customMileage,
      }),
    [
      routeDefaultMileage,
      routeMileage,
      routeCustomMileage,
      routeBilledMiles,
      totalMiles,
      useCustomMileage,
      customMileage,
    ]
  );

  // Display the numeric value in the input for easy editing/typing.
  const [displayValue, setDisplayValue] = useState(() => {
    return getCurrentMileageValue({
      route,
      totalMiles,
      useCustomMileage,
      customMileage,
      mileageSelectionType,
    }).toString();
  });

  // Keep display in sync when props change (e.g. extraction populates data)
  useEffect(() => {
    const nextValue = getCurrentMileageValue({
      route,
      totalMiles,
      useCustomMileage,
      customMileage,
      mileageSelectionType,
    });
    setDisplayValue(nextValue.toString());
  }, [
    routeDefaultMileage,
    routeMileage,
    routeCustomMileage,
    routeBilledMiles,
    totalMiles,
    useCustomMileage,
    customMileage,
    mileageSelectionType,
  ]);

  const handleChange = (e) => {
    const inputValue = e.value || e.target?.value || displayValue;
    const stringValue =
      typeof inputValue === "string" ? inputValue : inputValue.toString();
    const numValue = parseFloat(stringValue);

    const matchingOption = mileageOptions.find(
      (opt) => opt.value === numValue || opt.value.toString() === stringValue
    );

    if (matchingOption) {
      setDisplayValue(matchingOption.value.toString());
      onChange?.({
        value: {
          value: matchingOption.value,
          isRouteMileage: matchingOption.id === "defaultMileage",
          mileageType: matchingOption.id,
        },
      });
      return;
    }

    if (!isNaN(numValue) && numValue >= 0 && stringValue.trim().length > 0) {
      setDisplayValue(stringValue);
      onChange?.({
        value: {
          value: numValue,
          isRouteMileage: false,
          mileageType: "customInput",
        },
      });
    }
  };

  /**
   * Handles full text search in the mileage selector dropdown.
   * Filters options and resolves the mileage value from the search input.
   * Only calls onChange ONCE with the resolved value to prevent unnecessary
   * rerenders in EditableText (which was causing the parent form to flicker).
   */
  const handleFullTextSearch = (searchValue) => {
    setDisplayValue(searchValue);

    const filteredOptions = mileageOptions.filter((option) => {
      const value = searchValue.toUpperCase();
      const optionValue = option.value
        ? option.value.toString().toUpperCase()
        : "";
      const optionLabel = option.label ? option.label.toUpperCase() : "";

      if (!value) return true;
      if (optionValue.includes(value)) return true;
      if (optionLabel.includes(value)) return true;
      return false;
    });

    // Resolve the mileage value and notify parent with a single onChange call
    const numValue = parseFloat(searchValue);
    const matchingOption = mileageOptions.find(
      (opt) => opt.value === numValue || opt.value.toString() === searchValue
    );

    if (matchingOption) {
      onChange?.({
        value: {
          value: matchingOption.value,
          isRouteMileage: matchingOption.id === "defaultMileage",
          mileageType: matchingOption.id,
        },
      });
    } else if (
      !isNaN(numValue) &&
      numValue >= 0 &&
      searchValue.trim().length > 0
    ) {
      onChange?.({
        value: {
          value: numValue,
          isRouteMileage: false,
          mileageType: "customInput",
        },
      });
    } else {
      // Keep editable value in sync while typing (save happens on checkmark)
      onChange?.({ value: searchValue, pending: true });
    }

    return filteredOptions;
  };

  return (
    <div className="w-full">
      <Select
        size={size}
        value={displayValue}
        onChange={handleChange}
        search={true}
        fullTextSearch={handleFullTextSearch}
        // Show the numeric mileage in the input (not the option label),
        // otherwise the Select will set input text to the label on click
        // and then "bounce" back to the controlled numeric value.
        displayValueFrom="value"
        options={mileageOptions}
        placeholder="Select or type total mileage"
        label={showLabel ? "Total Mileage" : undefined}
      />
    </div>
  );
};

export default LegMileageSelector;
